// ORACLE FoodRx™ — CEO Nutrition Intelligence Command™
// Tier-gated. Education-only. No autonomous food intervention.
import React, { useCallback, useEffect, useState } from "react";
import { SHELL } from "@/api/connectorPackClient";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
async function api(path, opts = {}) {
  const res = await fetch(`${BACKEND}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...(opts.headers || {}) } });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

const DISCLAIMER = "FoodRx™ Intelligence is educational only and is not medical advice. It does not diagnose, treat, prescribe, or replace a clinician, registered dietitian, or pharmacist. Medication-food interactions must be confirmed with a licensed clinician or pharmacist.";

function ComplianceLock() {
  return (
    <div data-testid="foodrx-compliance-lock" style={{ marginTop: 24, padding: 14, background: "rgba(229,162,58,0.08)", border: "1px solid #E5A23A", borderRadius: 10, color: "#dfe3eb", fontSize: 12.5, lineHeight: 1.6 }}>
      <strong style={{ color: "#E5A23A" }}>Compliance lock:</strong> {DISCLAIMER}
    </div>
  );
}

export default function OracleFoodRxPage() {
  const [tier, setTier]         = useState("executive");
  const [routine, setRoutine]   = useState(null);
  const [week, setWeek]         = useState(null);
  const [interactions, setInt]  = useState(null);
  const [foods, setFoods]       = useState("grapefruit juice, spinach");
  const [meds, setMeds]         = useState("Rinvoq, warfarin");
  const [err, setErr]           = useState("");

  const load = useCallback(async () => {
    setErr("");
    try {
      const [r, w] = await Promise.all([
        api(`/api/oracle/foodrx/ceo-routine?tier=${encodeURIComponent(tier)}`),
        api(`/api/oracle/foodrx/weekly-plan?tier=${encodeURIComponent(tier)}`),
      ]);
      setRoutine(r);
      setWeek(w);
    } catch (e) { setErr(String(e.message || e)); }
  }, [tier]);
  useEffect(() => { load(); }, [load]);

  const runInteraction = useCallback(async () => {
    setErr("");
    try {
      const data = await api("/api/oracle/foodrx/interaction-check", {
        method: "POST",
        body: JSON.stringify({
          foods:       foods.split(",").map((s) => s.trim()).filter(Boolean),
          medications: meds.split(",").map((s) => s.trim()).filter(Boolean),
        }),
      });
      setInt(data);
    } catch (e) { setErr(String(e.message || e)); }
  }, [foods, meds]);

  return (
    <div style={SHELL.page} data-testid="oracle-foodrx-page">
      <h2 style={SHELL.h2}>ORACLE · CEO Nutrition Intelligence Command™</h2>
      <h1 style={SHELL.h1}>ORACLE FoodRx™</h1>
      <p style={SHELL.dim}>Tier-gated · Education-only · No autonomous food ordering, purchase, or delivery.</p>

      {/* Tier selector */}
      <div style={SHELL.panel}>
        <h2 style={SHELL.h2}>Membership Tier</h2>
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {["guest", "professional", "executive", "enterprise"].map((t) => (
            <button key={t} type="button" style={SHELL.btn(t === tier)} onClick={() => setTier(t)} data-testid={`foodrx-tier-${t}`}>
              {t.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {err && <div style={SHELL.err} data-testid="foodrx-err">{err}</div>}

      {/* Quick actions */}
      <div style={SHELL.panel} data-testid="foodrx-quick-actions">
        <h2 style={SHELL.h2}>Quick Actions</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {["FoodRx™ CEO Routine", "Today's Meal Plan", "Hydration Check", "Medication-Food Alert",
            "Crohn's-Safe Food Check", "Eczema Trigger Check", "West African Meal Plan",
            "LOGIX-2 FoodRx Coach", "Schedule FoodRx Reminders", "Weekly FoodRx Report"
          ].map((label) => (
            <span key={label} style={SHELL.badge()} data-testid={`foodrx-action-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>{label}</span>
          ))}
        </div>
      </div>

      {/* CEO routine */}
      <div style={SHELL.panel} data-testid="foodrx-ceo-routine">
        <h2 style={SHELL.h2}>CEO Daily Routine ({tier})</h2>
        {!routine?.allowed ? (
          <div style={SHELL.dim}>{routine?.public_education || "Loading..."}</div>
        ) : (
          <table style={{ ...SHELL.table, marginTop: 8 }}>
            <thead><tr><th style={SHELL.th}>Time</th><th style={SHELL.th}>Block</th><th style={SHELL.th}>Options</th></tr></thead>
            <tbody>
              {(routine.routine || []).map((b) => (
                <tr key={b.time}>
                  <td style={SHELL.td}>{b.time}</td>
                  <td style={SHELL.td}>{b.block}</td>
                  <td style={SHELL.td}>{(b.options || []).join(" · ") || b.prompt || b.structure}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Weekly plan */}
      <div style={SHELL.panel} data-testid="foodrx-weekly-plan">
        <h2 style={SHELL.h2}>West African Executive Meal Plan</h2>
        {!week?.allowed ? (
          <div style={SHELL.dim}>{week?.public_education || "Loading..."}</div>
        ) : (
          <table style={{ ...SHELL.table, marginTop: 8 }}>
            <thead><tr><th style={SHELL.th}>Day</th><th style={SHELL.th}>Breakfast</th><th style={SHELL.th}>Lunch</th><th style={SHELL.th}>Dinner</th></tr></thead>
            <tbody>
              {Object.entries(week.week || {}).map(([day, m]) => (
                <tr key={day}>
                  <td style={SHELL.td}>{day}</td>
                  <td style={SHELL.td}>{m.breakfast}</td>
                  <td style={SHELL.td}>{m.lunch}</td>
                  <td style={SHELL.td}>{m.dinner}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Interaction checker */}
      <div style={SHELL.panel} data-testid="foodrx-interaction-check">
        <h2 style={SHELL.h2}>Medication-Food Interaction Check (advisory)</h2>
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <input type="text" value={foods} onChange={(e) => setFoods(e.target.value)} placeholder="foods (comma-separated)"
                 style={{ flex: 1, minWidth: 240, background: "rgba(0,0,0,0.4)", border: "1px solid rgba(122,200,96,0.22)", color: "#dfe3eb", padding: "8px 12px", borderRadius: 6, fontSize: 13 }}
                 data-testid="foodrx-foods-input" />
          <input type="text" value={meds} onChange={(e) => setMeds(e.target.value)} placeholder="medications (comma-separated)"
                 style={{ flex: 1, minWidth: 240, background: "rgba(0,0,0,0.4)", border: "1px solid rgba(122,200,96,0.22)", color: "#dfe3eb", padding: "8px 12px", borderRadius: 6, fontSize: 13 }}
                 data-testid="foodrx-meds-input" />
          <button type="button" style={SHELL.btn(true)} onClick={runInteraction} data-testid="foodrx-check-btn">Check</button>
        </div>
        {interactions && (
          <div style={{ marginTop: 12 }} data-testid="foodrx-interaction-result">
            {(interactions.alerts || []).length === 0 ? (
              <div style={SHELL.dim}>No known interactions on file for that combination. Confirm with clinician/pharmacist for completeness.</div>
            ) : (
              (interactions.alerts || []).map((a, i) => (
                <div key={i} style={{ ...SHELL.dim, marginTop: 6, color: "#E5A23A" }}>
                  <strong>{a.medication.toUpperCase()}</strong> · {a.level.toUpperCase()} · {a.note}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <ComplianceLock />
    </div>
  );
}
